import { zCurrency } from "@redotech/money/usd-amounts";
import { privacy } from "@redotech/schema-privacy";
import { isEnumValue } from "@redotech/util/enum";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { BillingChargeSubtype } from "../unified-billing/enums";

export enum OutboundLabelsTransactionType {
  VOID_LABEL = "VOID_LABEL",
  VOID_USED_LABEL = "VOID_USED_LABEL",
  CHARGE_LABEL = "CHARGE_LABEL",
  ADD_FUNDS = "ADD_FUNDS",
  REFUND = "REFUND",
  MISCELLANEOUS = "MISCELLANEOUS",
  SURCHARGE = "SURCHARGE",
  RETURN_LABEL = "RETURN_LABEL",
  MERCHANT_CARRIER_FEE = "MERCHANT_CARRIER_FEE",
  SUBSCRIPTION_FEE = "SUBSCRIPTION_FEE",
  REDEEM_POINTS = "REDEEM_POINTS",
  INSURANCE = "INSURANCE",
  VOID_INSURANCE = "VOID_INSURANCE",
  TRANSACTION_FAILURE_CLAWBACK = "TRANSACTION_FAILURE_CLAWBACK",
  WMS_FEE = "WMS_FEE",
  REDO_SUPPLY = "REDO_SUPPLY",
  LANDED_COST_DUTIES = "LANDED_COST_DUTIES",
  LANDED_COST_TAXES = "LANDED_COST_TAXES",
  LANDED_COST_FEES = "LANDED_COST_FEES",
  VOID_LANDED_COST_DUTIES = "VOID_LANDED_COST_DUTIES",
  VOID_LANDED_COST_TAXES = "VOID_LANDED_COST_TAXES",
  VOID_LANDED_COST_FEES = "VOID_LANDED_COST_FEES",
  FREIGHT_PURCHASE = "FREIGHT_PURCHASE",
  VOID_FREIGHT_PURCHASE = "VOID_FREIGHT_PURCHASE",
  CARRIER_PICKUP = "CARRIER_PICKUP",
  CLAIM_PAYOUT = "CLAIM_PAYOUT",
  // Redo's commission debit when the carrier pays the merchant's own account directly.
  CLAIM_COMMISSION = "CLAIM_COMMISSION",
  REDUCE_BALANCE = "REDUCE_BALANCE",
  PROMOTIONAL_CREDIT = "PROMOTIONAL_CREDIT",
  USPS_PC_POSTAGE_FEE = "USPS_PC_POSTAGE_FEE",
  PERSONALIZED_INSERT_FEE = "PERSONALIZED_INSERT_FEE",
  PERSONALIZED_INSERT_REVENUE_SHARE = "PERSONALIZED_INSERT_REVENUE_SHARE",
  REDENOMINATION = "REDENOMINATION",
  CREDIT_CARD_FEE = "CREDIT_CARD_FEE",
}

export const transactionTypeLabels: Record<
  OutboundLabelsTransactionType,
  string
> = {
  [OutboundLabelsTransactionType.VOID_LABEL]: "Void Label",
  [OutboundLabelsTransactionType.VOID_USED_LABEL]: "Used Voided Label",
  [OutboundLabelsTransactionType.CHARGE_LABEL]: "Charge Label",
  [OutboundLabelsTransactionType.ADD_FUNDS]: "Add Funds",
  [OutboundLabelsTransactionType.REFUND]: "Refund",
  [OutboundLabelsTransactionType.MISCELLANEOUS]: "Miscellaneous",
  [OutboundLabelsTransactionType.SURCHARGE]: "Adjustment",
  [OutboundLabelsTransactionType.RETURN_LABEL]: "Return Label",
  [OutboundLabelsTransactionType.MERCHANT_CARRIER_FEE]: "Merchant Carrier Fee",
  [OutboundLabelsTransactionType.SUBSCRIPTION_FEE]: "Subscription Fee",
  [OutboundLabelsTransactionType.REDEEM_POINTS]: "Redeem Points",
  [OutboundLabelsTransactionType.INSURANCE]: "Insurance",
  [OutboundLabelsTransactionType.VOID_INSURANCE]: "Void Insurance",
  [OutboundLabelsTransactionType.TRANSACTION_FAILURE_CLAWBACK]:
    "Transaction Failure Clawback",
  [OutboundLabelsTransactionType.WMS_FEE]: "WMS Fee",
  [OutboundLabelsTransactionType.REDO_SUPPLY]: "Redo Supply",
  [OutboundLabelsTransactionType.LANDED_COST_DUTIES]: "Landed Cost Duties",
  [OutboundLabelsTransactionType.LANDED_COST_TAXES]: "Landed Cost Taxes",
  [OutboundLabelsTransactionType.LANDED_COST_FEES]: "Landed Cost Fees",
  [OutboundLabelsTransactionType.VOID_LANDED_COST_DUTIES]:
    "Void Landed Cost Duties",
  [OutboundLabelsTransactionType.VOID_LANDED_COST_TAXES]:
    "Void Landed Cost Taxes",
  [OutboundLabelsTransactionType.VOID_LANDED_COST_FEES]:
    "Void Landed Cost Fees",
  [OutboundLabelsTransactionType.FREIGHT_PURCHASE]: "Freight Purchase",
  [OutboundLabelsTransactionType.VOID_FREIGHT_PURCHASE]:
    "Void Freight Purchase",
  [OutboundLabelsTransactionType.CARRIER_PICKUP]: "Carrier Pickup",
  [OutboundLabelsTransactionType.CLAIM_PAYOUT]: "Claim payout",
  [OutboundLabelsTransactionType.CLAIM_COMMISSION]: "Claim Commission",
  [OutboundLabelsTransactionType.REDUCE_BALANCE]: "Reduce Balance",
  [OutboundLabelsTransactionType.PROMOTIONAL_CREDIT]: "Promotional Credit",
  [OutboundLabelsTransactionType.USPS_PC_POSTAGE_FEE]: "USPS PC Postage Fee",
  [OutboundLabelsTransactionType.PERSONALIZED_INSERT_FEE]:
    "Personalized Insert Fee",
  [OutboundLabelsTransactionType.PERSONALIZED_INSERT_REVENUE_SHARE]:
    "Personalized Insert Revenue Share",
  [OutboundLabelsTransactionType.REDENOMINATION]: "Currency Redenomination",
  [OutboundLabelsTransactionType.CREDIT_CARD_FEE]: "Credit Card Processing Fee",
};

/**
 * Reverse map from BillingChargeSubtype to OutboundLabelsTransactionType for
 * backwards compatibility with the mongo balance transaction system. Shared by
 * the admin balance-credit handler and the Add Balance Transaction UI.
 * Delete this when the mongo balance system is removed.
 */
export const CHARGE_SUBTYPE_TO_OUTBOUND_LABEL_TYPE: Partial<
  Record<BillingChargeSubtype, OutboundLabelsTransactionType>
> = {
  [BillingChargeSubtype.ChargeLabel]:
    OutboundLabelsTransactionType.CHARGE_LABEL,
  [BillingChargeSubtype.VoidLabel]: OutboundLabelsTransactionType.VOID_LABEL,
  [BillingChargeSubtype.VoidUsedLabel]:
    OutboundLabelsTransactionType.VOID_USED_LABEL,
  [BillingChargeSubtype.Refund]: OutboundLabelsTransactionType.REFUND,
  [BillingChargeSubtype.ClaimPayout]:
    OutboundLabelsTransactionType.CLAIM_PAYOUT,
  [BillingChargeSubtype.ClaimCommission]:
    OutboundLabelsTransactionType.CLAIM_COMMISSION,
  [BillingChargeSubtype.Miscellaneous]:
    OutboundLabelsTransactionType.MISCELLANEOUS,
  [BillingChargeSubtype.Surcharge]: OutboundLabelsTransactionType.SURCHARGE,
  [BillingChargeSubtype.ReturnLabel]:
    OutboundLabelsTransactionType.RETURN_LABEL,
  [BillingChargeSubtype.MerchantCarrierFee]:
    OutboundLabelsTransactionType.MERCHANT_CARRIER_FEE,
  [BillingChargeSubtype.SubscriptionFee]:
    OutboundLabelsTransactionType.SUBSCRIPTION_FEE,
  [BillingChargeSubtype.RedeemPoints]:
    OutboundLabelsTransactionType.REDEEM_POINTS,
  [BillingChargeSubtype.Insurance]: OutboundLabelsTransactionType.INSURANCE,
  [BillingChargeSubtype.VoidInsurance]:
    OutboundLabelsTransactionType.VOID_INSURANCE,
  [BillingChargeSubtype.TransactionFailureClawback]:
    OutboundLabelsTransactionType.TRANSACTION_FAILURE_CLAWBACK,
  [BillingChargeSubtype.WmsFee]: OutboundLabelsTransactionType.WMS_FEE,
  [BillingChargeSubtype.RedoSupply]: OutboundLabelsTransactionType.REDO_SUPPLY,
  [BillingChargeSubtype.LandedCostDuties]:
    OutboundLabelsTransactionType.LANDED_COST_DUTIES,
  [BillingChargeSubtype.LandedCostTaxes]:
    OutboundLabelsTransactionType.LANDED_COST_TAXES,
  [BillingChargeSubtype.LandedCostFees]:
    OutboundLabelsTransactionType.LANDED_COST_FEES,
  [BillingChargeSubtype.VoidLandedCostDuties]:
    OutboundLabelsTransactionType.VOID_LANDED_COST_DUTIES,
  [BillingChargeSubtype.VoidLandedCostTaxes]:
    OutboundLabelsTransactionType.VOID_LANDED_COST_TAXES,
  [BillingChargeSubtype.VoidLandedCostFees]:
    OutboundLabelsTransactionType.VOID_LANDED_COST_FEES,
  [BillingChargeSubtype.FreightPurchase]:
    OutboundLabelsTransactionType.FREIGHT_PURCHASE,
  [BillingChargeSubtype.VoidFreightPurchase]:
    OutboundLabelsTransactionType.VOID_FREIGHT_PURCHASE,
  [BillingChargeSubtype.CarrierPickup]:
    OutboundLabelsTransactionType.CARRIER_PICKUP,
  [BillingChargeSubtype.ManualAdjustment]:
    OutboundLabelsTransactionType.ADD_FUNDS,
  [BillingChargeSubtype.ReduceBalance]:
    OutboundLabelsTransactionType.REDUCE_BALANCE,
  [BillingChargeSubtype.PromotionalCredit]:
    OutboundLabelsTransactionType.PROMOTIONAL_CREDIT,
  [BillingChargeSubtype.UspsPcPostageFee]:
    OutboundLabelsTransactionType.USPS_PC_POSTAGE_FEE,
  [BillingChargeSubtype.PersonalizedInsertFee]:
    OutboundLabelsTransactionType.PERSONALIZED_INSERT_FEE,
  [BillingChargeSubtype.PersonalizedInsertRevenueShare]:
    OutboundLabelsTransactionType.PERSONALIZED_INSERT_REVENUE_SHARE,
  [BillingChargeSubtype.CardProcessingFee]:
    OutboundLabelsTransactionType.CREDIT_CARD_FEE,
};

export function getTransactionTypeLabel(
  transactionType: OutboundLabelsTransactionType | string,
): string {
  if (isEnumValue(transactionType, OutboundLabelsTransactionType)) {
    return transactionTypeLabels[
      transactionType as OutboundLabelsTransactionType
    ];
  }
  return transactionType;
}

export const BaseBalanceTransactionSchema = z.object({
  amount: z.string().meta({ privacy: privacy.nonPii }),
  fee: z.string().meta({ privacy: privacy.nonPii }), // something like a credit card fee that does not apply to their available balance
  currency: zCurrency.meta({ privacy: privacy.nonPii }),
  description: z.string().meta({ privacy: privacy.redact }),
  paymentIntentId: z.string().meta({ privacy: privacy.nonPii }).nullable(),
  paymentDetails: z
    .object({
      institution: z.string().nullish().meta({ privacy: privacy.redact }),
      last4: z.string().nullish().meta({ privacy: privacy.redact }),
    })
    .meta({ privacy: privacy.structured })
    .nullish(),
  status: z.string().meta({ privacy: privacy.nonPii }),
  createdAt: z.date().meta({ privacy: privacy.nonPii }),
  balanceSnapshot: z.string().meta({ privacy: privacy.nonPii }).nullish(),
});

// Map settings types to a Mongoose model and a Typescript interface
export const balanceTransactionSchemas = {
  "outbound-labels": BaseBalanceTransactionSchema.extend({
    orderId: z.string().nullish().meta({ privacy: privacy.nonPii }),
    shipmentId: z.string().nullish().meta({ privacy: privacy.nonPii }),
    redoShipmentId: z.string().nullish().meta({ privacy: privacy.nonPii }),
    batchId: z.string().nullish().meta({ privacy: privacy.nonPii }),
    transactionType: createEnum(OutboundLabelsTransactionType, {
      enumName: "OutboundLabelsTransactionType",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    justification: z.string().nullish().meta({ privacy: privacy.free_text }),
    // Since all carriers have a different way of presenting surcharges, we need key to prevent duplicate charges.
    uniqueIdentifier: z.string().nullish().meta({ privacy: privacy.nonPii }),
    chargeDate: z.date().nullish().meta({ privacy: privacy.nonPii }),
    pointsTransactionId: z.string().nullish().meta({ privacy: privacy.nonPii }),
    pointsEarned: z.number().nullish().meta({ privacy: privacy.nonPii }),
    fulfillmentGroupId: z.string().nullish().meta({ privacy: privacy.nonPii }),
    orderNames: z
      .array(z.string().meta({ privacy: privacy.nonPii }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    batchName: z.string().nullish().meta({ privacy: privacy.nonPii }),
    recognitionDate: z.date().nullish().meta({ privacy: privacy.nonPii }),
    // The CRDB billing_charge idempotency key for this transaction. Populated
    // by the live dual-write so the migration can produce a matching key when
    // backfilling later (otherwise the two writers diverge and produce 2×
    // duplicate billing_charge rows per logical event). Nullish for historical
    // transactions written before this field existed — those still need
    // targeted dedupe.
    idempotencyKey: z.string().nullish().meta({ privacy: privacy.nonPii }),
  }),
} as const;

export type balanceTransactionTypes = {
  [key in keyof typeof balanceTransactionSchemas]: z.infer<
    (typeof balanceTransactionSchemas)[key]
  >;
};

// This is necessary for backwards compatibility with old transactions
export function getOldTransactionType(
  description: string,
): OutboundLabelsTransactionType {
  if (
    description.includes("Auto-reload") ||
    description.includes("Outbound credit") ||
    description.includes("Balance credit outbound labels")
  ) {
    return OutboundLabelsTransactionType.ADD_FUNDS;
  }
  if (description.includes("Charging label")) {
    return OutboundLabelsTransactionType.CHARGE_LABEL;
  }
  if (description.includes("Refunding label")) {
    return OutboundLabelsTransactionType.VOID_LABEL;
  }
  if (description.includes("Refund")) {
    return OutboundLabelsTransactionType.REFUND;
  }
  return OutboundLabelsTransactionType.MISCELLANEOUS;
}
